import React, { useState, useEffect } from "react";
import { Box, Button, Typography } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeHighlight from "rehype-highlight";
import { Link as RouterLink } from "react-router-dom";
import { ExternalLinkIcon } from "@ippon/shared-assets";
import { colors } from "../theme/colors";
import { FilePreview } from "./CustomFilePreview";
import { ReferencedDocuments } from "./CustomReferencedDocuments";
import type { Message as MessageType } from "./AIChatTypes";

interface MessageProps {
  message: MessageType;
  primaryColor?: string;
  enableTypewriter?: boolean;
  showImagePreview?: boolean;
  onConfirmAction?: (messageId: string, actionId: string) => void;
  onRejectAction?: (messageId: string, actionId: string) => void;
}

const formatDate = (date: Date): string => {
  return date
    .toLocaleDateString("ja-JP", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    })
    .replace(/\//g, "/");
};

const formatTime = (date: Date): string => {
  return date.toLocaleTimeString("ja-JP", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const markdownStyles = (primaryColor: string) => ({
  "& p": {
    margin: 0,
    marginBottom: 1,
    fontSize: 14,
    color: colors.text.primary,
    lineHeight: 1.6,
    wordBreak: "break-word",
    overflowWrap: "break-word",
  },
  "& p:last-child": {
    marginBottom: 0,
  },
  "& ul, & ol": {
    margin: 0,
    marginBottom: 1,
    paddingLeft: 2,
    fontSize: 14,
    color: colors.text.primary,
    lineHeight: 1.6,
  },
  "& li": {
    marginBottom: 0.5,
  },
  "& h1, & h2, & h3, & h4, & h5, & h6": {
    margin: 0,
    marginTop: 1.5,
    marginBottom: 1,
    fontSize: 14,
    fontWeight: 600,
    color: colors.text.primary,
    lineHeight: 1.6,
  },
  "& h1:first-child, & h2:first-child, & h3:first-child, & h4:first-child, & h5:first-child, & h6:first-child":
    {
      marginTop: 0,
    },
  "& code": {
    backgroundColor: colors.background.light,
    padding: "2px 6px",
    borderRadius: "4px",
    fontSize: 13,
    fontFamily: "monospace",
    color: colors.text.primary,
  },
  "& pre": {
    backgroundColor: colors.background.light,
    padding: 1,
    borderRadius: "8px",
    overflow: "auto",
    margin: 0,
    marginBottom: 1,
    fontSize: 13,
    lineHeight: 1.6,
    "& code": {
      backgroundColor: "transparent",
      padding: 0,
    },
  },
  "& pre.hljs, & code.hljs": {
    backgroundColor: colors.background.light,
    color: colors.text.primary,
  },
  "& .hljs-keyword": { color: "#0066CC", fontWeight: 600 },
  "& .hljs-string": { color: "#008000" },
  "& .hljs-comment": { color: "#808080", fontStyle: "italic" },
  "& .hljs-number": { color: "#FF6600" },
  "& .hljs-function": { color: "#0066CC" },
  "& .hljs-variable": { color: "#0066CC" },
  "& .hljs-title": { color: "#0066CC", fontWeight: 600 },
  "& .hljs-type": { color: "#0066CC" },
  "& .hljs-built_in": { color: "#0066CC" },
  "& .hljs-literal": { color: "#FF6600" },
  "& .hljs-operator": { color: "#666666" },
  "& .hljs-punctuation": { color: "#666666" },
  "& blockquote": {
    borderLeft: "3px solid #E0E0E0",
    paddingLeft: 1.5,
    margin: 0,
    marginBottom: 1,
    color: "#666666",
    fontSize: 14,
    lineHeight: 1.6,
  },
  "& table": {
    borderCollapse: "collapse",
    width: "100%",
    marginBottom: 1,
    fontSize: 14,
  },
  "& th, & td": {
    border: "1px solid #E0E0E0",
    padding: "8px 12px",
    textAlign: "left",
  },
  "& th": {
    backgroundColor: colors.background.light,
    fontWeight: 600,
  },
  "& a": {
    color: primaryColor,
    textDecoration: "none",
    display: "inline-flex",
    alignItems: "center",
    gap: "2px",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  "& hr": {
    border: "none",
    borderTop: "1px solid #E0E0E0",
    margin: "16px 0",
  },
});

const preprocessMarkdown = (content: string): string => {
  if (!content) return "";
  const parts = content.split(/(`{3}[\s\S]*?`{3}|`[^`]*`)/g);

  return parts
    .map((part) => {
      if (part.startsWith("`")) {
        return part;
      }
      // 1) **bold** の前後 whitespace を除去
      let out = part
        .replace(/\*\*\s+(.+?)\s+\*\*/g, "**$1**")
        .replace(/\*\*\s+(.+?)\*\*/g, "**$1**")
        .replace(/\*\*(.+?)\s+\*\*/g, "**$1**");

      // 2) AI が箇条書きの改行を落として `text-text-text` のように 1 行に潰した場合の
      //    救済。日本語文字に挟まれた "-" は本来 list bullet のはずなので改行 + "- " に
      //    変換する。日付 (2026-05-24) や英数字 ID (test-case) は無傷で残す。
      out = out.replace(
        /([\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Han}ー])-(?=[\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Han}])/gu,
        "$1\n- ",
      );

      return out;
    })
    .join("");
};

const isInternalHref = (href: string | undefined): href is string =>
  typeof href === "string" && href.startsWith("/") && !href.startsWith("//");

const SmartLink: React.FC<
  React.AnchorHTMLAttributes<HTMLAnchorElement> & { children?: React.ReactNode }
> = ({ href, children, ...props }) => {
  if (isInternalHref(href)) {
    return (
      <RouterLink to={href} {...(props as any)}>
        {children}
        <ArrowForwardIcon
          sx={{ fontSize: 14, ml: "2px", verticalAlign: "text-bottom" }}
        />
      </RouterLink>
    );
  }
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
      {children}
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          ml: "2px",
          verticalAlign: "text-bottom",
          "& svg": {
            width: 14,
            height: 14,
          },
        }}
      >
        <ExternalLinkIcon />
      </Box>
    </a>
  );
};

const markdownComponents = {
  a: SmartLink as any,
};

const getPendingActionLabel = (
  status: NonNullable<MessageType["pendingAction"]>["status"],
): string => {
  switch (status) {
    case "executing":
      return "処理中です";
    case "executed":
      return "実行済み";
    case "rejected":
      return "キャンセル済み";
    case "failed":
      return "処理に失敗しました";
    default:
      return "承認待ち";
  }
};


export const Message: React.FC<MessageProps> = ({
  message,
  primaryColor = colors.primary.main,
  enableTypewriter = false,
  showImagePreview = false,
  onConfirmAction,
  onRejectAction,
}) => {
  const isUser = message.sender === "user";
  // Preprocess content to fix markdown issues
  const cleanContent = React.useMemo(
    () => preprocessMarkdown(message.content),
    [message.content],
  );
  const [displayedContent, setDisplayedContent] = useState(cleanContent);
  const [lastTypedId, setLastTypedId] = useState<string>("");
  const pendingAction = message.pendingAction;
  const isActionPending = pendingAction?.status === "pending";
  const isActionExecuting = pendingAction?.status === "executing";

  // タイプライター効果
  useEffect(() => {
    // タイプライター効果を適用しない場合
    // タイプライター効果を適用しない場合
    if (!enableTypewriter || isUser) {
      setDisplayedContent(cleanContent);
      setLastTypedId(message.id);
      return;
    }

    // 既にこのメッセージでタイプライター効果を実行済みの場合
    if (lastTypedId === message.id) {
      return;
    }

    // タイプライター効果を実行
    setDisplayedContent("");
    setLastTypedId(message.id);

    let currentIndex = 0;
    const interval = setInterval(() => {
      if (currentIndex < cleanContent.length) {
        setDisplayedContent(cleanContent.slice(0, currentIndex + 1));
        currentIndex++;
      } else {
        clearInterval(interval);
      }
    }, 20); // 20msごとに1文字追加

    return () => clearInterval(interval);
  }, [message.id, enableTypewriter]); // message.idとenableTypewriterの変更時のみ

  if (!isUser) {
    // Bot message (no bubble, ChatGPT style)
    return (
      <Box sx={{ display: "flex", gap: 2 }}>
        <Box sx={{ flex: 1, maxWidth: "100%", overflow: "hidden" }}>
          {message.senderName && (
            <Typography
              sx={{ mb: 1, fontSize: 14, color: colors.text.primary }}
            >
              {message.senderName}
            </Typography>
          )}

          {/* File Attachments */}
          {message.files && message.files.length > 0 && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                maxWidth: "100%",
                alignSelf: "stretch",
              }}
            >
              {message.files.map((file) => (
                <FilePreview
                  key={file.id}
                  file={file}
                  primaryColor={primaryColor}
                  showImagePreview={showImagePreview}
                  variant={showImagePreview ? "imageCard" : "compact"}
                />
              ))}
            </Box>
          )}

          <Box sx={markdownStyles(primaryColor)}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeHighlight]}
              components={markdownComponents}
            >
              {displayedContent}
            </ReactMarkdown>
          </Box>

          {pendingAction && (
            <Box
              sx={{
                mt: 2,
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 1,
              }}
            >
              {(isActionPending || isActionExecuting) &&
              onConfirmAction &&
              onRejectAction ? (
                <>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<CheckCircleOutlineIcon />}
                    disabled={isActionExecuting}
                    onClick={() =>
                      onConfirmAction(message.id, pendingAction.actionId)
                    }
                    sx={{
                      borderRadius: "8px",
                      boxShadow: "none",
                      backgroundColor: primaryColor,
                      textTransform: "none",
                      "&:hover": {
                        backgroundColor: primaryColor,
                        boxShadow: "none",
                        opacity: 0.9,
                      },
                    }}
                  >
                    {isActionExecuting ? "処理中" : "承認して実行"}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={isActionExecuting}
                    onClick={() =>
                      onRejectAction(message.id, pendingAction.actionId)
                    }
                    sx={{
                      borderRadius: "8px",
                      textTransform: "none",
                      borderColor: "#D0D5DD",
                      color: colors.text.secondary,
                    }}
                  >
                    キャンセル
                  </Button>
                </>
              ) : (
                <Typography
                  sx={{
                    fontSize: 13,
                    color:
                      pendingAction.status === "failed"
                        ? colors.status.error
                        : "#667085",
                  }}
                >
                  {getPendingActionLabel(pendingAction.status)}
                </Typography>
              )}
            </Box>
          )}

          {/* Referenced Documents */}
          {message.referencedDocuments &&
            message.referencedDocuments.length > 0 && (
              <ReferencedDocuments
                documents={message.referencedDocuments}
                primaryColor={primaryColor}
                showLabels={true}
              />
            )}

          {/* Timestamp */}
          <Typography sx={{ mt: 1, fontSize: 12, color: "#989898" }}>
            {formatDate(message.timestamp)} {formatTime(message.timestamp)}
          </Typography>
        </Box>
      </Box>
    );
  }

  // User message (with bubble)
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        gap: 1,
      }}
    >
      {/* File Attachments */}
      {message.files && message.files.length > 0 && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            maxWidth: "70%",
            width: "fit-content",
          }}
        >
          {message.files.map((file) => (
            <FilePreview
              key={file.id}
              file={file}
              primaryColor={primaryColor}
              showImagePreview={showImagePreview}
              variant={showImagePreview ? "imageCard" : "compact"}
            />
          ))}
        </Box>
      )}

      <Box
        sx={{
          borderTopLeftRadius: 10,
          borderTopRightRadius: 0,
          borderBottomLeftRadius: 10,
          borderBottomRightRadius: 10,
          backgroundColor: "#DBF8FF",
          px: 2,
          py: 1.5,
          maxWidth: "70%",
          overflow: "hidden",
        }}
      >
        <Box sx={markdownStyles(primaryColor)}>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeHighlight]}
            components={markdownComponents}
          >
            {cleanContent}
          </ReactMarkdown>
        </Box>
      </Box>

      {/* Referenced Documents */}
      {message.referencedDocuments &&
        message.referencedDocuments.length > 0 && (
          <Box
            sx={{
              maxWidth: "70%",
              width: "fit-content",
            }}
          >
            <ReferencedDocuments
              documents={message.referencedDocuments}
              primaryColor={primaryColor}
              showLabels={false}
            />
          </Box>
        )}

      {/* Timestamp */}
      <Typography sx={{ fontSize: 12, color: "#989898" }}>
        {formatDate(message.timestamp)} {formatTime(message.timestamp)}
      </Typography>
    </Box>
  );
};
